// Production target-metric registry (#4009/#4004, epic #4002).
//
// `TargetMetricRegistryEntry` existed as a lib TYPE with spec-fixture-only values;
// nothing in production carried the metricFamily that
// `validateContributionContractOverlapKeys` needs, so no CI path could run the
// overlap-key vocabulary fence over the real manifests. This file anchors the
// canonical entries.
//
// EXTEND-NOT-FORK LAW: this registry is the metric-FAMILY layer over the existing
// `OBJECTIVE_VALUE_BRIDGE_METRIC_REGISTRY_SEED` in `packages/lib/src/metric-registry.ts`
// — it never mints a metric id of its own. Every entry's `metricId` must resolve
// through `resolveMetricTargetReference` at the same registry version, and every
// seed entry at that version must have exactly one entry here (bijection enforced
// by `target-metric-registry.spec.ts` and `scripts/check-metric-registry.mjs`).
// Add a metric by extending the seed first; then mirror it here with its family.
//
// ID CONVENTION (pinned): metric ids here are the BARE-FORM snake_case ids the
// contract-bearing manifests write in `targetMetricRef.metricId`
// ('operating_income', not 'operating_income_usd'). The taxonomy-map id system
// `metric:operating_income:v1` (colon-delimited, versioned — see
// `metric:sga_cost:v1` in the conversation-bridge spec fixtures) is a DIFFERENT
// id system; cross-reference only, no unification attempted here.
//
// DERIVE-DON'T-INVENT: every field value below is extracted from existing code and
// cited inline. Metric families in particular are never guessed:
// - a family is uniquely determined when the metric's contract overlap keys occur
//   in exactly one family vocabulary of OVERLAP_DIMENSION_KEYS_BY_METRIC_FAMILY;
// - otherwise the family follows the only production pin that exists for the
//   metric's series (see operating_income below).
import type {
  ObjectiveConstraintId,
  TargetMetricRegistryEntry,
} from './objective-value-bridge';
import { OBJECTIVE_CONSTRAINT_IDS } from './objective-value-bridge';

// Same version string the ten contract-bearing manifests and the
// OBJECTIVE_VALUE_BRIDGE_METRIC_REGISTRY_SEED both use.
export const TARGET_METRIC_REGISTRY_VERSION = 'objective_value_bridge_v1';

export type TargetMetricRegistryVersion = typeof TARGET_METRIC_REGISTRY_VERSION;

// No per-metric constraint narrowing exists anywhere in code today; narrowing the
// closed OBJECTIVE_CONSTRAINT_IDS catalog per metric without a source would be
// invention, so every entry allows the full closed catalog until a governed
// mapping exists.
const ALL_OBJECTIVE_CONSTRAINTS: readonly ObjectiveConstraintId[] =
  OBJECTIVE_CONSTRAINT_IDS;

export const TARGET_METRIC_REGISTRY: readonly TargetMetricRegistryEntry[] =
  Object.freeze([
    Object.freeze({
      // Seed: metric-registry.ts operating_income_usd, alias 'operating_income'
      // ("objective-value-bridge bare-string target id + 3PL defaultObjectiveMetric").
      metricId: 'operating_income',
      label: 'Operating income',
      // Family pin: the only production family binding on the operating-income
      // series is the attribution never-sum golden oracle in approved-formulas.ts
      // (governedSeriesId 'derived_ebitda_from_operating_income'): metricFamily
      // 'cost' with overlapDimensionKeys ['costPoolIds']. The composite EBITDA
      // aggregator likewise de-conflicts operating-income claims over attributed
      // cost_pool atoms, and the plurality of contract-bearing manifests targeting
      // operating_income declare the cost vocabulary (workforce-reduction,
      // sga-spend, segment-margin-cleanup, productivity-lean-ai: ['costPoolIds']).
      // Contracts carrying revenue/margin-vocabulary keys against this target are
      // pinned as named DEBT in contribution-contract.conformance.spec.ts — the
      // rule is not softened to absorb them.
      metricFamily: 'cost',
      unit: 'USD',
      directionSemantics: Object.freeze({
        // Seed direction 'higher_is_better' (metricEntry default on
        // operating_income_usd): increases improve the objective.
        increaseMeans: 'improves_objective',
        decreaseMeans: 'worsens_objective',
        allowedDirections: Object.freeze(['increase', 'maintain'] as const),
      }),
      baselineSource: Object.freeze({
        // finance.manifest.ts finance_read_operating_income ("What is the governed
        // operating-income baseline?"), formulasUsed 'operating_income_derive_v1',
        // evidence 'approved_finance_pnl_artifact'.
        anchorModuleId: 'finance',
        anchorIntentId: 'finance_read_operating_income',
        formulaRef: 'operating_income_derive_v1',
        requiredEvidenceKey: 'approved_finance_pnl_artifact',
      }),
      // The decision modules whose manifests declare a contribution contract with
      // targetMetricRef.metricId 'operating_income' (existing module-id catalog;
      // no fourth lever-branch catalog is minted).
      allowedLeverBranches: Object.freeze([
        'carrier-transportation-cost',
        'cost-to-serve',
        'pricing-realization',
        'productivity-lean-ai',
        'risk-claims',
        'segment-margin-cleanup',
        'sga-spend',
        'workforce-reduction',
      ]),
      allowedConstraints: ALL_OBJECTIVE_CONSTRAINTS,
      defaultAnchorMetricId: 'operating_income',
      // Substitution-with-disclosure exists in production for this series:
      // finance answers adjusted-operating-income and EBITDA phrasings from the
      // operating-income anchor (finance_read_target_gap examples;
      // 'ebitda_from_operating_income_v1' in finance formulasUsed).
      askVsAnchorPolicy: 'anchorable_substitution_allowed',
    }),
    Object.freeze({
      // Seed: metric-registry.ts objective_gap_usd, alias 'objective_value_gap'
      // (code-wins delta: objective_gap_usd is the live metricKey; the manifests
      // write the alias as the bare-form target id).
      metricId: 'objective_value_gap',
      label: 'Objective value gap',
      // Uniquely determined by the closed vocabulary: its only contract
      // (finance.manifest.ts) declares ['glAccountIds'], and 'glAccountIds'
      // occurs in exactly one family vocabulary — cost.
      metricFamily: 'cost',
      unit: 'USD',
      directionSemantics: Object.freeze({
        // Seed signConvention { kind: 'lower_is_better' }; the finance contract
        // declares signConvention 'negative_improves_metric'.
        increaseMeans: 'worsens_objective',
        decreaseMeans: 'improves_objective',
        allowedDirections: Object.freeze(['decrease', 'maintain'] as const),
      }),
      baselineSource: Object.freeze({
        // finance.manifest.ts finance_read_target_gap (contract-bearing target-gap
        // intent); seed composedFrom formulaId 'objective_value_gap_v1'; first
        // contract requiredEvidenceKey.
        anchorModuleId: 'finance',
        anchorIntentId: 'finance_read_target_gap',
        formulaRef: 'objective_value_gap_v1',
        requiredEvidenceKey: 'approved_finance_pnl_artifact',
      }),
      // Only finance declares a contribution contract to this target.
      allowedLeverBranches: Object.freeze(['finance']),
      allowedConstraints: ALL_OBJECTIVE_CONSTRAINTS,
      defaultAnchorMetricId: 'objective_value_gap',
      askVsAnchorPolicy: 'same_metric_required',
    }),
    Object.freeze({
      // Seed: metric-registry.ts cash_flow_release (bare-form is already the seed
      // metricKey; no alias). check-metric-registry.mjs pins that no manifest
      // targetMetricRef uses it today — it is reachable via impactMetricKey only.
      metricId: 'cash_flow_release',
      label: 'Cash flow release',
      // Family: cash-track members claim "cash release, not operating income"
      // (namesake-economics.conformance.spec.ts); 'cash' is the family whose
      // vocabulary (cashFlowLineIds, balanceSheetAccountIds) carries
      // cash-flow/balance-sheet dimensions.
      metricFamily: 'cash',
      unit: 'USD',
      directionSemantics: Object.freeze({
        // Seed direction 'higher_is_better'.
        increaseMeans: 'improves_objective',
        decreaseMeans: 'worsens_objective',
        allowedDirections: Object.freeze(['increase', 'maintain'] as const),
      }),
      baselineSource: Object.freeze({
        // OWNERSHIP REPOINT 2026-08-04 (#4449): the baseline moved from
        // capex-discipline to capital-allocation.
        //
        // The seed's original source locator named capex-discipline.engine.ts
        // (#impactMetricKey:cash_flow_release). capex-discipline ships a stub
        // proof bundle (zero status:'ok' paths) and is slated for retirement;
        // anchoring a bridge-seed metric on a stub left the entire cash track
        // of the objective bridge unbacked. capital-allocation is the real
        // owner: a KEEP wedge module whose 'capex_size_value_at_stake' intent
        // declares a governed contribution to THIS target metric
        // (targetMetricRef.metricId 'cash_flow_release'), sized by an approved
        // formula against approved evidence.
        //
        // Fields below are the capital-allocation contract's own declarations
        // (CAPITAL_ALLOCATION_FREED_CASH_CONTRIBUTION): the sizing intent, its
        // single declared formulaRef, and the first of its requiredEvidenceKeys
        // — the same "first contract requiredEvidenceKey" convention the
        // objective_value_gap entry above follows.
        anchorModuleId: 'capital-allocation',
        anchorIntentId: 'capex_size_value_at_stake',
        formulaRef: 'capex_reprioritization_value_v1',
        requiredEvidenceKey: 'project_cashflow_forecast',
      }),
      // The modules that carry a governed claim on this target.
      //
      // capital-allocation ADMITTED 2026-08-04 (#4449). Its manifest previously
      // recorded a deliberate exclusion ("the registry lists the impactMetricKey
      // emitters only ... until the registry adjudicates freed capital into the
      // cash bridge"); this entry is that adjudication. Admission does NOT relax
      // any never-sum fence (AGENTS.md Law 8): capital_freed_usd stays
      // publishability 'candidate_only', never composites additively, and the
      // engine's own working-capital shared-scope overlap receipt gate still
      // fail-closes publication. allowedLeverBranches governs which modules may
      // be decomposed to for this target — not whether their claims may be summed.
      //
      // capex-discipline and working-capital were removed 2026-08-05 (#4449)
      // when their stub module trees were retired. capital-allocation is now
      // the sole branch — which is exactly why the anchor had to move first
      // (#4468): retiring both prior emitters while they still held the anchor
      // would have emptied this list and tripped the length > 0 guard in
      // target-metric-registry.spec.ts.
      allowedLeverBranches: Object.freeze(['capital-allocation']),
      allowedConstraints: ALL_OBJECTIVE_CONSTRAINTS,
      defaultAnchorMetricId: 'cash_flow_release',
      askVsAnchorPolicy: 'same_metric_required',
    }),
    Object.freeze({
      // Seed: metric-registry.ts strategic_execution_capacity_gap (bare-form is
      // the seed metricKey; no alias).
      metricId: 'strategic_execution_capacity_gap',
      label: 'Strategic execution capacity gap',
      // Uniquely determined by the closed vocabulary: its only contract declares
      // ['roleFamilyIds', 'processIds'], and both keys occur in exactly one
      // family vocabulary — capacity.
      metricFamily: 'capacity',
      // The contract declares unit 'COUNT' (seed unit 'fte', temporalType
      // 'count': an FTE shortfall, never a dollar total).
      unit: 'COUNT',
      directionSemantics: Object.freeze({
        // Seed direction 'lower_is_better'; contract signConvention
        // 'negative_improves_metric'.
        increaseMeans: 'worsens_objective',
        decreaseMeans: 'improves_objective',
        allowedDirections: Object.freeze(['decrease', 'maintain'] as const),
      }),
      baselineSource: Object.freeze({
        // Seed ownerModuleId; the module's readout intent; the contract's
        // formulaRef; STRATEGIC_EXECUTION_CAPACITY_EVIDENCE_KEYS.gapSnapshot.
        anchorModuleId: 'strategic-execution-capacity-diagnostic',
        anchorIntentId: 'strategic_execution_capacity_readout',
        formulaRef: 'strategic_execution_capacity_utilization@1.0.0',
        requiredEvidenceKey: 'strategic_execution_gap_snapshot',
      }),
      allowedLeverBranches: Object.freeze([
        'strategic-execution-capacity-diagnostic',
      ]),
      allowedConstraints: ALL_OBJECTIVE_CONSTRAINTS,
      defaultAnchorMetricId: 'strategic_execution_capacity_gap',
      askVsAnchorPolicy: 'same_metric_required',
    }),
  ]) satisfies readonly TargetMetricRegistryEntry[];

// Fail-closed resolver over the anchored registry: exact bare-form id at the
// exact registry version, or undefined. No alias minting here — alias
// resolution stays owned by resolveMetricTargetReference over the seed; the
// bijection spec keeps the two mechanisms pointing at the same metrics.
export function resolveTargetMetricRegistryEntry(
  reference: Readonly<{ metricId: string; registryVersion: string }>,
): TargetMetricRegistryEntry | undefined {
  if (reference.registryVersion !== TARGET_METRIC_REGISTRY_VERSION) {
    return undefined;
  }
  return TARGET_METRIC_REGISTRY.find(
    (entry) => entry.metricId === reference.metricId,
  );
}
